import { ArrowLeftIcon, ShoppingCartIcon, TrashIcon } from '@heroicons/react/20/solid';
import { useFetcher } from '@remix-run/react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useLocale } from 'remix-i18next/react';
import { useActionUrlWithOrigin } from '#common/hooks/useActionUrlWithOrigin.ts';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { getMediumTitle } from '#common/medium-display';
import { minimalTakeSchema, renderHiddenFieldsFromDiscriminatedUnion } from '#common/models/tts-models';
import { useTranslatedPath } from '#common/utils/i18n.js';
import { useAffiliateRef } from '#common/utils/tracking/affiliate-ref-context.tsx';
import { type ResolvedAvailableUse } from '#speaker-shop/components/shop-layouts/types.ts';
import { calculateUnifiedPrice, getAdditionalPricePerCharacter } from '#speaker-shop/models/price-calculation';
import { CouponEntry, type CouponInfo } from './CouponEntry';
import { useRecordingContext } from './RecordingContext';

interface Props {
    availableUse: ResolvedAvailableUse;
    currency: string;
    onBack?: () => void;
}

/**
 * v2-styled inline checkout for the unified single-page shop. Reuses the standard
 * `/api/initiate-purchase` flow (same hidden fields as {@link OrderCard}) but renders
 * with the Core v2 design tokens and applies aggregate-character pricing.
 */
export function CoreCheckout({ availableUse, currency, onBack }: Props) {
    const { t, rawT } = useEditableTranslation('checkout');
    const { t: tc, rawT: rawTc } = useEditableTranslation('core-shop');
    const { t: tMedium } = useEditableTranslation('medium-selection');
    const locale = useLocale();
    const translatePath = useTranslatedPath(locale);
    const affiliateRef = useAffiliateRef();
    const initiatePurchaseUrl = useActionUrlWithOrigin('/api/initiate-purchase');
    const fetcher = useFetcher();
    const { recordings, setCheckState } = useRecordingContext();
    const [ acceptTerms, setAcceptTerms ] = useState(false);
    const [ appliedCoupon, setAppliedCoupon ] = useState<CouponInfo | null>(null);
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    const allTakes = useMemo(() => recordings.flatMap(r => r.takes), [ recordings ]);
    const checkedCount = useMemo(() => allTakes.filter(x => x.isChecked).length, [ allTakes ]);
    const selectedRecordings = useMemo(() =>
        recordings.map((recording, recordingIndex) => {
            const selectedTakes = recording.takes.filter(take => take.isChecked);
            const details = calculateUnifiedPrice([ { takes: selectedTakes } ], availableUse);
            return { recording, recordingIndex, selectedTakes, details };
        }).filter(recording => recording.selectedTakes.length > 0), [ recordings, availableUse ]);

    const priceDetails = useMemo(
        () =>
            calculateUnifiedPrice(
                recordings.map(recording => ({ takes: recording.takes.filter(take => take.isChecked) })),
                availableUse
            ),
        [ recordings, availableUse ]
    );
    const price = priceDetails.price;
    const additionalPricePerCharacter = getAdditionalPricePerCharacter(availableUse);

    const discountedPrice = useMemo(
        () => (appliedCoupon?.type === 'percentage' ? price * (1 - appliedCoupon.discount / 100) : price),
        [ appliedCoupon, price ]
    );

    const mediumName = getMediumTitle(availableUse.medium, locale);
    const licenseDuration = availableUse.timeframeUnit
        ? tMedium('restricted-usage', {
            value: availableUse.timeframeValue || 1,
            unit: availableUse.timeframeUnit
        })
        : tMedium('unrestricted-usage');
    const allowOrder = acceptTerms && checkedCount > 0;

    useEffect(() => {
        if (fetcher.state === 'idle') {
            setIsSubmitting(false);
        }
    }, [ fetcher.state ]);

    return (
        <div className='core-card p-4 sm:p-6'>
            <h2 className='mb-4 text-lg font-bold'>{tc('checkout.summary-title')}</h2>
            <div className='mb-5'>
                <div className='mb-4 space-y-2'>
                    {selectedRecordings.map(({ recording, recordingIndex, selectedTakes, details }) => (
                        <div
                            className='grid grid-cols-1 items-center gap-2 rounded-md bg-[var(--core-soft-bg)] px-3 py-2.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-3'
                            key={recording.takes[0]?.takeId ?? recordingIndex}
                        >
                            <div className='min-w-0'>
                                <div className='text-[11px] font-bold uppercase text-[var(--core-blue)]'>
                                    {tc('checkout.recording-details', {
                                        number: recordingIndex + 1,
                                        characters: details.totalCharacters,
                                        count: selectedTakes.length
                                    })}
                                </div>
                                <div className='truncate text-sm font-semibold' title={recording.text}>
                                    {recording.text}
                                </div>
                                <div className='mt-1 text-[11px] opacity-70'>
                                    {tc('checkout.base-price-details', {
                                        currency,
                                        price: availableUse.basePrice.toFixed(2),
                                        included: availableUse.basePriceCharacters,
                                        rate: additionalPricePerCharacter.toFixed(2)
                                    })}
                                </div>
                                {details.additionalCharacters > 0 && (
                                    <div className='text-[11px] font-semibold text-[var(--core-accent)]'>
                                        {tc('checkout.additional-price-details', {
                                            basePrice: availableUse.basePrice.toFixed(2),
                                            characters: details.additionalCharacters,
                                            currency,
                                            rate: additionalPricePerCharacter.toFixed(2),
                                            totalPrice: details.price.toFixed(2)
                                        })}
                                    </div>
                                )}
                            </div>
                            <div className='flex items-center justify-between gap-3 whitespace-nowrap text-sm sm:justify-start'>
                                <strong>{currency} {details.price.toFixed(2)}</strong>
                                <button
                                    type='button'
                                    className='p-1 opacity-70 transition hover:text-red-400 hover:opacity-100'
                                    onClick={() => selectedTakes.forEach(take => setCheckState(take.takeId, false))}
                                    aria-label={rawTc('checkout.remove-recording', { number: recordingIndex + 1 })}
                                >
                                    <TrashIcon className='size-4' />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <SummaryRow label={tc('checkout.medium')} value={mediumName} />
                <SummaryRow label={tc('checkout.license')} value={licenseDuration} />
                <div className='flex items-end justify-between gap-4 pt-4 text-lg font-bold'>
                    <span>{tc('checkout.total')}</span>
                    <div className='text-right'>
                        {appliedCoupon && (
                            <div className='text-xs font-normal opacity-45 line-through'>
                                {currency} {price.toFixed(2)}
                            </div>
                        )}
                        <div className='text-[26px] text-[var(--core-accent)]'>
                            {currency} {discountedPrice.toFixed(2)}
                        </div>
                        <div className='text-[11px] font-normal opacity-50'>{tc('checkout.excl-vat')}</div>
                    </div>
                </div>
            </div>

            <ul className='mb-5 space-y-2 border-t border-[var(--core-muted-border)] pt-4'>
                {[ t('voice-owner-approved'), t('including-release-document'), t('download-after-payment') ].map(
                    label => (
                        <li
                            className="relative pl-5 text-xs before:absolute before:left-0 before:font-bold before:text-[var(--core-blue)] before:content-['✓']"
                            key={label as unknown as string}
                        >
                            {label}
                        </li>
                    )
                )}
                <li className="relative pl-5 text-xs before:absolute before:left-0 before:font-bold before:text-[var(--core-blue)] before:content-['✓']">
                    {t('format')}: {t('common:formats.mp3')}, 128 kBit/s
                </li>
            </ul>

            <fetcher.Form method='POST' action={initiatePurchaseUrl} onSubmit={() => setIsSubmitting(true)}>
                {affiliateRef && <input type='hidden' name='ref' value={affiliateRef} />}
                <input type='hidden' name='currency' value={currency} />
                <input type='hidden' name='allowedMediums[0]' value={availableUse.id} />
                {recordings.map((recording, i) =>
                    recording.takes.filter(take => take.isChecked).map((take, j) => (
                        <Fragment key={take.takeId}>
                            {renderHiddenFieldsFromDiscriminatedUnion({
                                discriminator: 'voiceType',
                                namePrefix: `recordings[${i}][takes][${j}]`,
                                schema: minimalTakeSchema,
                                value: take
                            })}
                        </Fragment>
                    ))
                )}
                <div className='mb-3.5'>
                    <CouponEntry onCouponApplied={setAppliedCoupon} checkboxClassName='text-xs' nativeCheckbox={true} />
                </div>
                <label className='mb-3.5 flex items-center gap-2 text-xs'>
                    <input
                        type='checkbox'
                        name='acceptTerms'
                        className='accent-cvyellow'
                        checked={acceptTerms}
                        onChange={e => setAcceptTerms(e.target.checked)}
                    />
                    <span>
                        <Trans t={rawT} i18nKey='accept-terms'>
                            <a
                                href={translatePath('/gtc')}
                                target='_blank'
                                rel='noreferrer'
                                className='text-[var(--core-text)] underline'
                            >
                            </a>
                        </Trans>
                    </span>
                </label>
                <button
                    className='core-primary-button w-full py-3.5 text-base'
                    type='submit'
                    disabled={!allowOrder || isSubmitting}
                >
                    <ShoppingCartIcon className='size-5' /> {tc('checkout.buy')}
                </button>
            </fetcher.Form>
            {onBack && (
                <button className='core-back-button mt-4' type='button' onClick={onBack}>
                    <ArrowLeftIcon className='size-4' /> {tc('order.back')}
                </button>
            )}
        </div>
    );
}

function SummaryRow({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
    return (
        <div className='flex justify-between gap-4 border-b border-[var(--core-soft-border)] py-2 text-sm opacity-75'>
            <span>{label}</span>
            <span className='text-right'>{value}</span>
        </div>
    );
}
